/**
 * Pure rules of a workflow card shared by every client: what a choice sends (`workflow_reply` +
 * the visible message), whether a typed value is acceptable, whether the card is still open and
 * what it does to the composer. No React, no DOM.
 */
import {
  WORKFLOW_CAPS,
  WORKFLOW_CODE_LENGTH,
  WORKFLOW_COMMENT_MAX_CHARS,
  WORKFLOW_RATING_STARS,
  WORKFLOW_RATING_THUMBS,
  WORKFLOW_SEND_CODE_BUTTONS,
  WORKFLOW_SKIP_TEXT,
  WORKFLOW_VALUE_MAX_CHARS,
  type WorkflowAnswer,
  type WorkflowChoice,
  type WorkflowComposer,
  type WorkflowField,
  type WorkflowFieldType,
  type WorkflowOption,
  type WorkflowRatingScale,
  type WorkflowRatingThumb,
  type WorkflowReply,
  type WorkflowRunRef,
  type WorkflowStepKind,
  type WorkflowStepPayload,
  type WorkflowTicketValues,
} from "./contract";
import type { ChatMessage } from "../types";
import { DEFAULT_WORKFLOW_VERIFY_LABELS, type WorkflowVerifyLabels } from "./labels";

/** Why a typed value is refused; the card maps each reason to its own copy. */
export type WorkflowValueIssue = "required" | "email" | "phone" | "number" | "date" | "option";

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_CHARS = /^\+?[0-9\s().-]+$/;
const PHONE_FORMATTING = /[\s().-]/g;
const PHONE_MIN_DIGITS = 7;
const PHONE_MAX_DIGITS = 15;
const NUMBER = /^-?\d+(\.\d+)?$/;
const DIGITS = /^\d+$/;
const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
/** A code may be typed with spaces or dashes (`482 913`, `482-913`). */
const CODE_SEPARATORS = /[\s-]/g;
/** The visible chat message of a code that was sent: the code itself only travels as the `workflow_reply` value. */
export const WORKFLOW_CODE_MASK = "•";

function isRealDate(raw: string): boolean {
  const match = ISO_DATE.exec(raw);
  if (!match) return false;
  const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
  const date = new Date(Date.UTC(year, month - 1, day));
  return date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day;
}

/** Loose E.164: optional leading +, 7 to 15 digits, spaces / dashes / dots / brackets allowed. */
function isPhone(raw: string): boolean {
  if (!PHONE_CHARS.test(raw)) return false;
  const digits = raw.replace(/\D/g, "").length;
  return digits >= PHONE_MIN_DIGITS && digits <= PHONE_MAX_DIGITS;
}

/** The phone as the server stores it: formatting removed, a leading + kept. */
export function compactPhone(raw: string): string {
  return raw.trim().replace(PHONE_FORMATTING, "");
}

/** Null when `raw` is an acceptable answer to `field`; otherwise the reason it is not. */
export function validateWorkflowValue(field: Pick<WorkflowField, "type" | "required" | "options">, raw: string): WorkflowValueIssue | null {
  const value = raw.trim();
  if (value === "") return field.required ? "required" : null;
  switch (field.type) {
    case "email":
      return EMAIL.test(value) ? null : "email";
    case "phone":
      return isPhone(value) ? null : "phone";
    case "number":
      return NUMBER.test(value) ? null : "number";
    case "date":
      return isRealDate(value) ? null : "date";
    case "dropdown":
      return (field.options ?? []).some((option) => option.id === value) ? null : "option";
    case "digits":
      return DIGITS.test(value.replace(/\s/g, "")) ? null : "number";
    case "text":
    case "any":
      return null;
  }
}

/** How a field's text control is set up (keyboard, autofill); a dropdown has no text control. */
export interface WorkflowInputTraits {
  type: "text" | "email" | "tel" | "date";
  inputMode?: "email" | "tel" | "decimal" | "numeric";
  autoComplete?: string;
}

const INPUT_TRAITS: Record<Exclude<WorkflowFieldType, "dropdown">, WorkflowInputTraits> = {
  text: { type: "text" },
  email: { type: "email", inputMode: "email", autoComplete: "email" },
  phone: { type: "tel", inputMode: "tel", autoComplete: "tel" },
  number: { type: "text", inputMode: "decimal" },
  date: { type: "date" },
  any: { type: "text" },
  digits: { type: "text", inputMode: "numeric" },
};

export function workflowInputTraits(type: Exclude<WorkflowFieldType, "dropdown">): WorkflowInputTraits {
  return INPUT_TRAITS[type];
}

/** Which button an answered card shows as picked (by id, else by the label the visitor tapped or typed). */
export function isChosenWorkflowButton(card: Pick<WorkflowStepPayload, "answer">, option: WorkflowOption): boolean {
  if (!card.answer) return false;
  return card.answer.button_id ? card.answer.button_id === option.id : card.answer.label === option.label;
}

const STAR_FILLED = "★";
const STAR_EMPTY = "☆";
const THUMB_TEXT: Record<WorkflowRatingThumb, string> = { up: "👍", down: "👎" };

/** The values a rating card offers, in display order: "1".."5" for stars, "up" | "down" for thumbs. */
export function workflowRatingValues(scale: WorkflowRatingScale): readonly string[] {
  return scale === "stars" ? Array.from({ length: WORKFLOW_RATING_STARS }, (_, index) => String(index + 1)) : WORKFLOW_RATING_THUMBS;
}

/** The visible chat message of a rating ("★★★★☆", the thumb emoji), or null when `value` is not on the scale. */
export function workflowRatingText(scale: WorkflowRatingScale, value: string): string | null {
  if (!workflowRatingValues(scale).includes(value)) return null;
  if (scale === "thumbs") return THUMB_TEXT[value as WorkflowRatingThumb];
  const filled = Number(value);
  return STAR_FILLED.repeat(filled) + STAR_EMPTY.repeat(WORKFLOW_RATING_STARS - filled);
}

function ratingFromText(scale: WorkflowRatingScale, text: string): string | null {
  if (scale === "thumbs") return WORKFLOW_RATING_THUMBS.find((thumb) => text.includes(THUMB_TEXT[thumb])) ?? null;
  const filled = text.split(STAR_FILLED).length - 1;
  return workflowRatingText(scale, String(filled)) === null ? null : String(filled);
}

/** The rating an answered card shows: the answer's value, else read back from its visible text ("★★★★☆"); null when unknown. */
export function answeredWorkflowRating(card: Pick<WorkflowStepPayload, "scale" | "answer">): string | null {
  const { answer } = card;
  if (!answer) return null;
  const value = answer.value?.trim() ?? "";
  if (workflowRatingText(card.scale, value) !== null) return value;
  return ratingFromText(card.scale, answer.label ?? "");
}

/** A visitor's answer as the chat request carries it: visible text + `workflow_reply`. */
export interface WorkflowAnswerRequest {
  text: string;
  reply: WorkflowReply;
}

/** Kinds a button answers: plain reply buttons, the code card's buttons and the ticket cards (Submit carries the card's values). */
const BUTTON_KINDS: ReadonlySet<WorkflowStepKind> = new Set<WorkflowStepKind>(["reply_buttons", "verify_email", "ticket_summary", "ticket_form", "ticket_duplicate"]);

function answerToButton(card: WorkflowStepPayload, buttonId: string, ticket?: WorkflowTicketValues): WorkflowAnswerRequest | null {
  const button = card.buttons.find((candidate) => candidate.id === buttonId);
  if (!button) return null;
  return { text: button.label, reply: { run_id: card.run_id, step_id: card.step_id, button_id: button.id, ...(ticket ? { ticket } : {}) } };
}

function answerToField(card: WorkflowStepPayload, raw: string, skipText: string): WorkflowAnswerRequest | null {
  const field = card.field;
  if (!field) return null;
  const typed = raw.trim().slice(0, WORKFLOW_VALUE_MAX_CHARS);
  const base = { run_id: card.run_id, step_id: card.step_id };
  if (typed === "") return field.required ? null : { text: skipText, reply: { ...base, value: "" } };
  if (validateWorkflowValue(field, typed) !== null) return null;
  if (field.type === "dropdown") {
    const option = (field.options ?? []).find((candidate) => candidate.id === typed);
    return option ? { text: option.label, reply: { ...base, value: option.id } } : null;
  }
  return { text: typed, reply: { ...base, value: field.type === "phone" ? compactPhone(typed) : typed } };
}

/** A reply typed in the composer: trimmed and cut like any chat message; the server checks the format. */
function answerToReply(card: WorkflowStepPayload, raw: string): WorkflowAnswerRequest | null {
  const typed = raw.trim().slice(0, WORKFLOW_VALUE_MAX_CHARS);
  if (typed === "") return null;
  return { text: typed, reply: { run_id: card.run_id, step_id: card.step_id, value: typed } };
}

/** The digits of a code typed into a `verify_email` card, or null when they are not exactly the digits the card asks for. */
export function normalizeWorkflowCode(raw: string, length: number = WORKFLOW_CODE_LENGTH): string | null {
  const code = raw.replace(CODE_SEPARATORS, "");
  return code.length === length && DIGITS.test(code) ? code : null;
}

/** A code for the card that waits for it; the chat shows dots, never the digits. */
function answerToCode(card: WorkflowStepPayload, raw: string): WorkflowAnswerRequest | null {
  if (card.otp?.stage !== "enter") return null;
  const code = normalizeWorkflowCode(raw, card.otp.length);
  if (code === null) return null;
  return { text: WORKFLOW_CODE_MASK.repeat(code.length), reply: { run_id: card.run_id, step_id: card.step_id, value: code } };
}

function answerToRating(card: WorkflowStepPayload, raw: string, comment: string | undefined): WorkflowAnswerRequest | null {
  const value = raw.trim();
  const text = workflowRatingText(card.scale, value);
  if (text === null) return null;
  const note = card.ask_comment ? (comment ?? "").trim().slice(0, WORKFLOW_COMMENT_MAX_CHARS) : "";
  return { text, reply: { run_id: card.run_id, step_id: card.step_id, value, ...(note ? { comment: note } : {}) } };
}

/**
 * What answering `card` with `choice` sends, or null when the choice does not belong to the card
 * (unknown button, empty required value, invalid value, a value off the rating scale, an empty
 * reply). The visible
 * `text` is the button label or the entered value (a dropdown shows the option label and sends the
 * option id; a rating shows its stars or thumb). A rating's comment is trimmed, cut to 500
 * characters and left out when empty or when the card did not ask for one.
 */
export function buildWorkflowAnswer(card: WorkflowStepPayload, choice: WorkflowChoice, skipText: string = WORKFLOW_SKIP_TEXT): WorkflowAnswerRequest | null {
  if ("button_id" in choice) return BUTTON_KINDS.has(card.kind) ? answerToButton(card, choice.button_id, choice.ticket) : null;
  if (card.kind === "verify_email") return answerToCode(card, choice.value);
  if (card.kind === "rating") return answerToRating(card, choice.value, choice.comment);
  if (card.kind === "collect_reply") return answerToReply(card, choice.value);
  return card.kind === "collect_data" ? answerToField(card, choice.value, skipText) : null;
}

/** The request body fields every client adds: `caps` always (a client that also draws the ticket cards passes its own list), `workflow_reply` when answering a card. */
export function workflowRequestFields(reply?: WorkflowReply, caps: readonly string[] = WORKFLOW_CAPS): { caps: string[]; workflow_reply?: WorkflowReply } {
  return reply ? { caps: [...caps], workflow_reply: reply } : { caps: [...caps] };
}

/** `expires_at` has passed (an unparseable or missing date never expires). */
export function isWorkflowExpired(card: Pick<WorkflowStepPayload, "expires_at">, now: number = Date.now()): boolean {
  if (!card.expires_at) return false;
  const at = Date.parse(card.expires_at);
  return Number.isFinite(at) && at <= now;
}

/** An assistant message whose workflow card has not been answered yet. */
export function hasOpenWorkflowCard(message: ChatMessage): message is ChatMessage & { action: { type: "workflow_step"; payload: WorkflowStepPayload } } {
  return message.action?.type === "workflow_step" && message.action.payload.state === "open";
}

/** The message with its open workflow card settled (answered), keeping `answer` when given. */
export function settleWorkflowCard(message: ChatMessage & { action: { type: "workflow_step"; payload: WorkflowStepPayload } }, answer?: WorkflowAnswer): ChatMessage {
  const { payload } = message.action;
  return { ...message, action: { type: "workflow_step", payload: { ...payload, state: "answered", answer: answer ?? payload.answer } } };
}

/**
 * A restored transcript: a card the visitor has since moved past (a later visitor message exists)
 * cannot be answered any more, whatever state the server stored.
 */
export function settleRestoredWorkflowCards(messages: ChatMessage[]): ChatMessage[] {
  const lastVisitor = messages.findLastIndex((message) => message.role === "user");
  return messages.map((message, index) => (index < lastVisitor && hasOpenWorkflowCard(message) ? settleWorkflowCard(message) : message));
}

/**
 * The card still waiting for an answer: the last message of the conversation is a finished
 * assistant message whose workflow card is open and not expired (once the visitor answers or types
 * something else, a card is settled by the reducer and nothing is open).
 */
export function openWorkflowCard(messages: readonly ChatMessage[], now: number = Date.now()): WorkflowStepPayload | undefined {
  const last = messages.at(-1);
  if (last?.role !== "assistant" || last.status !== "done" || last.action?.type !== "workflow_step") return undefined;
  const card = last.action.payload;
  return card.state === "open" && !isWorkflowExpired(card, now) ? card : undefined;
}

/**
 * A workflow's scripted line (a question with its card, a greeting, a nudge, a thank-you): nothing to rate
 * or follow up on, and the server refuses a vote on it. `workflowMessage` is the server's own mark (restored
 * transcript, live stream, the `done` of a turn); a turn this client just streamed without a model has the run id
 * and no `grounded`. An answer of the assistant (it carries `grounded`) keeps its feedback bar even when the
 * workflow ran around it.
 */
export function isScriptedWorkflowMessage(message: Pick<ChatMessage, "action" | "workflowRunId" | "workflowMessage" | "grounded">): boolean {
  if (message.action?.type === "workflow_step" || message.workflowMessage === true) return true;
  return message.workflowRunId !== undefined && message.grounded === undefined;
}

/** The conversation opens with a workflow's message (a started run): no separate welcome / greeting above it. */
export function isWorkflowLed(messages: readonly ChatMessage[]): boolean {
  const first = messages[0];
  return first?.role === "assistant" && (first.workflowRunId !== undefined || first.action?.type === "workflow_step");
}

/** Statuses of a run that still waits on the visitor or the assistant; any other has ended. */
const ACTIVE_RUN_STATUSES: ReadonlySet<string> = new Set(["running", "waiting", "delegated"]);

/** What a run asks of the composer: its own `composer` while it is active, `open` once it has ended. */
export function runComposer(run: Pick<WorkflowRunRef, "status" | "composer">): WorkflowComposer {
  return ACTIVE_RUN_STATUSES.has(run.status) ? run.composer : "open";
}

/** The conversation's active run restored from the transcript (`workflow` of `GET /public/v1/conversations/{id}`): its composer state goes back on the last message. */
export function applyRestoredWorkflow(messages: ChatMessage[], run: WorkflowRunRef | null | undefined): ChatMessage[] {
  if (!run) return messages;
  const composer = runComposer(run);
  const lastIndex = messages.length - 1;
  if (composer === "open" || messages[lastIndex]?.role !== "assistant") return messages;
  return messages.map((message, index) => (index === lastIndex ? { ...message, workflowRunId: run.run_id, workflowComposer: composer } : message));
}

/** What the composer does now and the open card behind it. */
export interface WorkflowComposerState {
  /** `open`: type anything; `locked`: typing is off (choose an option first); `reply`: the composer answers the open question. */
  mode: WorkflowComposer;
  /** The card waiting for an answer, if any. */
  card: WorkflowStepPayload | undefined;
}

/**
 * The composer's state: a `collect_reply` question open -> `reply`; the run (or the open card) locks
 * the composer, or the open card does not allow typing -> `locked`; otherwise `open`. A card the
 * visitor answered or that expired ends the run's say: nothing locks the composer then.
 */
export function workflowComposerState(messages: readonly ChatMessage[], now: number = Date.now()): WorkflowComposerState {
  const card = openWorkflowCard(messages, now);
  if (card?.kind === "collect_reply" || (card?.kind === "verify_email" && card.composer === "reply")) return { mode: "reply", card };
  const last = messages.at(-1);
  const closedCard = last?.action?.type === "workflow_step" && card === undefined;
  const runLocks = !closedCard && last?.role === "assistant" && last.status === "done" && last.workflowComposer === "locked";
  const locked = card?.composer === "locked" || card?.allow_typing === false || runLocks;
  return { mode: locked ? "locked" : "open", card };
}

/** The composer is locked while the run (or an open card that does not allow typing) says so. */
export function isComposerLockedByWorkflow(messages: readonly ChatMessage[], now: number = Date.now()): boolean {
  return workflowComposerState(messages, now).mode === "locked";
}

/** How the composer's single-line field is set up to answer a `collect_reply` question. */
export interface WorkflowReplyInput extends WorkflowInputTraits {
  /** The question (the field's accessible name is "Answer: <question>"). */
  question: string;
  maxLength: number;
}

/**
 * The input traits for `card` (a `collect_reply` or code card): keyboard and autofill by format, the question, the length limit.
 * A code card asks the browser for the one-time code it finds in the mail, and leaves room for the spaces a visitor may type.
 */
export function workflowReplyInput(card: Pick<WorkflowStepPayload, "field"> & Partial<Pick<WorkflowStepPayload, "kind" | "otp">>): WorkflowReplyInput {
  const field = card.field;
  const type = field && field.type !== "dropdown" ? field.type : "any";
  const traits = workflowInputTraits(type);
  if (card.kind === "verify_email") {
    const length = card.otp?.length ?? WORKFLOW_CODE_LENGTH;
    return { ...traits, inputMode: "numeric", autoComplete: "one-time-code", question: field?.label ?? "", maxLength: length * 2 };
  }
  const maxLength = Math.min(field?.max_length ?? WORKFLOW_VALUE_MAX_CHARS, WORKFLOW_VALUE_MAX_CHARS);
  return { ...traits, question: field?.label ?? "", maxLength };
}

/** Only digits, spaces and dashes: the visitor is typing a code, not a sentence. */
const CODE_SHAPED = /^[\d\s-]+$/;

/**
 * Why the composer holds back the text typed for an open question, in the host's words (null: send it). Only a code card
 * checks on the device, and only a mistyped code (digits, spaces and dashes of the wrong count): it is a typo, so it is not
 * sent and costs no try. Words are sent as a plain message - the server asks for the code again and, after five of them,
 * takes the step's failed path, which is the visitor's way out when no code arrives.
 */
export function workflowReplyIssue(card: Pick<WorkflowStepPayload, "kind" | "otp">, raw: string, labels?: Partial<Pick<WorkflowVerifyLabels, "invalidCode">>): string | null {
  if (card.kind !== "verify_email" || card.otp?.stage !== "enter") return null;
  if (!CODE_SHAPED.test(raw.trim()) || normalizeWorkflowCode(raw, card.otp.length) !== null) return null;
  return (labels?.invalidCode ?? DEFAULT_WORKFLOW_VERIFY_LABELS.invalidCode)(card.otp.length);
}

/** The buttons that make the server e-mail a code carry the bot check: true for "Send code" and "Resend code". */
export function isWorkflowCodeSend(choice: WorkflowChoice): boolean {
  return "button_id" in choice && (WORKFLOW_SEND_CODE_BUTTONS as readonly string[]).includes(choice.button_id);
}

/** Hosts hand `chat.send` to this: it sends the answer as a normal chat turn with its `workflow_reply` (and the bot-check token of "Send code" / "Resend code"). */
export type SendWithWorkflowReply = (text: string, options: { workflowReply: WorkflowReply; turnstileToken?: string }) => void;

/** What a host adds to an answer besides the choice: the single-use bot-check token of a button that makes the server e-mail a code. */
export interface WorkflowAnswerExtras {
  captchaToken?: string | null | undefined;
}

/** Answers a card through `send`; false when the choice is not valid for the card (nothing is sent). */
export function answerWorkflowStep(send: SendWithWorkflowReply, card: WorkflowStepPayload, choice: WorkflowChoice, skipText?: string, extras?: WorkflowAnswerExtras): boolean {
  const answer = buildWorkflowAnswer(card, choice, skipText);
  if (!answer) return false;
  const token = extras?.captchaToken;
  send(answer.text, token ? { workflowReply: answer.reply, turnstileToken: token } : { workflowReply: answer.reply });
  return true;
}
